// src/components/ProductCard.tsx

import React, { useEffect, useMemo, useState } from "react";
import Card from "./Card";
import { useNavigate } from "react-router-dom";
import {
  getTabLink,
  toUniqueList,
  getImagesByColor,
  sortColorsByGalleryOrder,
} from "../extensions/helpers";
import { CatalogObject } from "square";
import { useCatalog } from "../contexts/CatalogContext";
import ConfirmationModal from "./ConfirmationModal";
import { useSearch } from "../contexts/SearchContext";
import BubbleSelector from "./BubbleSelector";
import ProductCardImageCarousel from "./ProductCardImageCarousel";
import WishlistButton from "./WishlistButton";

type ProductCardProps = {
  product: CatalogObject;
  domain?: CatalogObject;
  category?: CatalogObject;
  onClick?: (product: CatalogObject) => void;
  isSmallVersion?: boolean;
  forceReload?: boolean;
  onRemoveProduct?: (id: string) => void;
};

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  domain,
  category,
  onClick,
  isSmallVersion = false,
  forceReload = false,
  onRemoveProduct,
}) => {
  const navigate = useNavigate();
  const {
    getImagesByIds,
    getLowestCategoryByItem,
    getCategoryById,
    getOptionDataByColor,
    colorOption,
  } = useCatalog();
  const { setIsSearchMenuOpen } = useSearch();
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [isConfirmationModelOpen, setIsConfirmationModelOpen] =
    useState<boolean>(false);

  const handleClickCard = () => {
    if (onClick != null) {
      onClick(product);
      return;
    }

    // The item's OWN lowest category wins over whatever page the card sits on,
    // so a card always links to the product's canonical URL — the one
    // sitemap.js emits and ProductPage/product-meta.js declare. The domain grid
    // (`/shop/apparel`) hands down the TOP-LEVEL category, and honouring that
    // minted `/shop/apparel/category/apparel/product/x`, a second working URL
    // for the same product.
    //
    // The props stay as a fallback rather than being withheld: an item filed
    // with no child category has no canonical URL (sitemap.js skips that shape
    // too), and dropping the segment entirely would emit
    // `/shop/apparel/product/x` — a path no route matches, i.e. a 404 on click.
    // A duplicate URL is a ranking nuisance; a dead link is a lost sale.
    //
    // Cards rendered from the Search menu get no props at all and rely on the
    // item lookup outright.
    //
    // Walked in the item's OWN category order, matching sitemap.js,
    // product-meta.js and ProductPage — getLowestCategoryByItem scans the
    // CATALOG's order instead, so an item carrying two child categories could
    // resolve to a different one here than the page canonicalizes to, and the
    // card would link at a URL that disowns itself. It stays as the second
    // fallback for a `product` passed without its categories embedded (the id
    // lookup finds the fuller object in the catalog). Every item is filed
    // under its domain as well as its child — "Apparel" is listed FIRST on all
    // 15 products — so the isTopLevel test is what makes this pick "Hats"
    // instead of "Apparel"; drop it and the walk stops on the first entry.
    const ownCategory = (product.itemData?.categories ?? [])
      .map((c) => getCategoryById(c.id ?? ""))
      .find((c) => c && !c.categoryData?.isTopLevel);
    const categoryObject =
      ownCategory ?? getLowestCategoryByItem(product.id ?? "") ?? category;
    const categoryName = categoryObject?.categoryData?.name ?? undefined;
    // Derive the domain from that same category so the two segments can't come
    // from different chains; the prop is the fallback here for the same reason.
    const domainName =
      getCategoryById(categoryObject?.categoryData?.parentCategory?.id ?? "")
        ?.categoryData?.name ??
      domain?.categoryData?.name ??
      undefined;

    setIsSearchMenuOpen(false);

    navigate(
      getTabLink({
        domain: domainName,
        category: categoryName ?? undefined,
        itemName: product.itemData?.name ?? undefined,
      }),
      {
        state: {
          selectedProduct: product,
          color: selectedColor,
        },
      },
    );
    if (forceReload) {
      window.location.reload();
    }
  };

  // All Product Variation Objects
  const productVariationObjects = useMemo(
    () => product?.itemData?.variations,
    [product?.itemData?.variations],
  );

  // All Product Item Variation
  const productVariations = useMemo(
    () => productVariationObjects?.flatMap((v) => v?.itemVariationData),
    [productVariationObjects],
  );

  // The item gallery, in the order the merchant arranged it in Square. This is
  // the ordering authority for both the swatches and each colour's photos, and
  // it lives on the ITEM — so it is present on the first render, unlike the
  // lazily-fetched image objects.
  const galleryImageIds = useMemo(
    () => product?.itemData?.imageIds ?? [],
    [product?.itemData?.imageIds],
  );

  // Available Product Colors
  const productColors = useMemo(() => {
    const vars = productVariations
      ?.flatMap((v) => v?.itemOptionValues)
      ?.filter((iov): iov is NonNullable<typeof iov> => !!iov?.itemOptionId)
      ?.filter((iov) => iov.itemOptionId!.includes(colorOption?.id ?? ""))
      ?.map((iov) => iov.itemOptionValueId);
    // Ordered by the item GALLERY, so the card leads with the colour whose
    // photo the merchant put first in Square, not whatever order Square returns
    // the variations in.
    return sortColorsByGalleryOrder(
      toUniqueList(vars ?? []),
      productVariations as any[],
      galleryImageIds,
    );
  }, [productVariations, colorOption, galleryImageIds]);

  // Given a Color, get the sorted images (delegates to shared helper)
  const getColorImages = (colorId: string) =>
    getImagesByColor(
      productVariations as any[],
      colorId,
      getImagesByIds,
      galleryImageIds,
    );

  const [selectedColor, setSelectedColor] = useState<string | null>(null);

  // Swatches shown on the card before collapsing into a "+N" chip: 2 on phone,
  // 4 on desktop. Keyed on the same 768px breakpoint where the grid widens from
  // 2 columns (grid-cols-2) to 3+ (md:grid-cols-3). The narrow phone tile only
  // fits 2 swatches + the chip on one line (the chip takes the 3rd slot).
  const [maxVisibleColors, setMaxVisibleColors] = useState<number>(
    typeof window !== "undefined" && window.innerWidth >= 768 ? 4 : 2,
  );
  useEffect(() => {
    const update = () => setMaxVisibleColors(window.innerWidth >= 768 ? 4 : 2);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Thumbnail for the compact (search) tile. The colour-keyed lookup returns
  // nothing when a search result's variations carry no image ids for the
  // selected colour, which left the tile blank; fall back to the item's own
  // images, then to any image on any variation.
  const smallImageUrl = useMemo(() => {
    const byColor = getImagesByColor(
      productVariations as any[],
      selectedColor ?? "",
      getImagesByIds,
      galleryImageIds,
    )?.[0]?.imageData?.url;
    if (byColor) return byColor;

    const fallbackIds = [
      ...(product?.itemData?.imageIds ?? []),
      ...(productVariations?.flatMap((v) => v?.imageIds ?? []) ?? []),
    ].filter(Boolean);
    return getImagesByIds(fallbackIds)?.[0]?.imageData?.url ?? undefined;
  }, [
    productVariations,
    selectedColor,
    getImagesByIds,
    galleryImageIds,
    product,
  ]);

  const priceCents = Number(
    product?.itemData?.variations?.[0]?.itemVariationData?.priceMoney?.amount,
  );

  useEffect(() => {
    if (!productColors.includes(selectedColor ?? "")) {
      setSelectedColor(productColors?.[0] ?? null);
    }
  }, [selectedColor, productColors, setSelectedColor]);

  return (
    <>
      <ConfirmationModal
        isOpen={isConfirmationModelOpen}
        onConfirm={() => onRemoveProduct && onRemoveProduct(product?.id ?? "")}
        onCancel={() => setIsConfirmationModelOpen(false)}
      >
        Are you sure you would like to remove this item?
      </ConfirmationModal>

      <Card className="hover:cursor-pointer">
        <div className="relative w-full aspect-[1] overflow-hidden">
          {/* Skeleton background. Suppressed when a compact tile has no image
              at all — otherwise onLoad never fires and it pulses forever. */}
          {!imageLoaded && !(isSmallVersion && !smallImageUrl) && (
            <div
              className={`absolute inset-0 animate-pulse bg-gray-100 shadow-md brightness-90`}
            />
          )}

          {/* The image itself */}
          <div
            className={`absolute inset-0 transition-all duration-500 ease-out transform ${
              imageLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
          >
            {/* Wishlist heart (hidden in Update Order mode, where the corner
                belongs to the remove button) */}
            {!onRemoveProduct && (
              <WishlistButton
                productId={product?.id}
                className="absolute top-2 right-2 z-10 bg-white rounded-full shadow p-1.5 hover:bg-gray-100"
              />
            )}

            {/* Remove Item Button - NOTE: Used for Update Order */}
            {!!onRemoveProduct && (
              <button
                onClick={() => setIsConfirmationModelOpen(true)}
                className="absolute top-2 right-2 z-10 bg-white rounded-full shadow p-1 hover:bg-gray-100 transition"
                aria-label="Remove Item"
              >
                ✕
              </button>
            )}

            {!isSmallVersion ? (
              <ProductCardImageCarousel
                productId={product?.id}
                selectedColor={selectedColor ?? undefined}
                colorAndImages={
                  productColors?.map((c) => ({
                    color: c ?? "",
                    images: getColorImages(c ?? "")?.map(
                      (i) => i.imageData?.url ?? undefined,
                    ),
                  })) ?? []
                }
                colorSelector={null}
                onClick={handleClickCard}
                onLoad={() => setTimeout(() => setImageLoaded(true), 100)}
              />
            ) : (
              <img
                src={smallImageUrl}
                alt={product?.itemData?.name ?? undefined}
                // object-contain: product shots are centered on white, and
                // cover crops the design out of a small thumbnail.
                className="w-full h-full object-contain bg-white"
                onLoad={() => setTimeout(() => setImageLoaded(true), 100)}
                onClick={handleClickCard}
              />
            )}
          </div>
        </div>

        {/* Text info. Rendered unconditionally in both variants — it used to
            wait on imageLoaded in the browse/carousel card, so the name and
            price popped in after the photo and shoved the swatch row down. */}
        {isSmallVersion ? (
          <div
            className="px-2 pb-2 pt-1 cursor-pointer"
            onClick={handleClickCard}
          >
            <h3
              title={product?.itemData?.name ?? undefined}
              className="text-sm font-semibold leading-snug text-brown-dark line-clamp-2"
            >
              {product?.itemData?.name}
            </h3>
            {Number.isFinite(priceCents) && (
              <p className="mt-0.5 text-sm text-gray-500 tabular-nums">
                ${(priceCents / 100).toFixed(2)}
              </p>
            )}
          </div>
        ) : (
          <div className="product-card-text" onClick={handleClickCard}>
            {/* Two lines, always — a name that fits on one still reserves the
                second, so every card in a grid row or carousel track lines up.
                The single truncated line this replaced cut most product names
                off mid-word. */}
            <h3
              title={product?.itemData?.name ?? undefined}
              className="product-card-name"
            >
              {product?.itemData?.name}
            </h3>
            <p className="product-card-price">
              ${(priceCents / 100).toFixed(2)}
            </p>
          </div>
        )}

        {/* Colour bubbles sit under the price, matching the browse layout, and
            are noise on a compact search tile. */}
        {!isSmallVersion && (
          <BubbleSelector
            selected={selectedColor}
            setSelected={(value) => {
              const newColor = value.id;
              // 1. Set Color
              setSelectedColor(newColor);
            }}
            options={productColors?.map((c) => ({
              id: c,
              name: getOptionDataByColor(c)?.itemOptionValueData?.name ?? "",
              color:
                getOptionDataByColor(c)?.itemOptionValueData?.color ??
                undefined,
            }))}
            disabledOptionExplanation="Out of stock"
            // Kept tight so both homepage carousels still fit on one phone
            // screen — the price and the swatches under it read as one block.
            className="product-card-swatches w-full"
            // Cap the browse-grid card to a single fixed-height row so cards
            // stay vertically aligned; the "+N" chip opens a popover listing
            // the hidden colours (selectable in place — it deliberately does
            // NOT navigate to the product page). 2 on phone, 4 on desktop.
            maxVisible={maxVisibleColors}
          />
        )}
      </Card>
    </>
  );
};

export default ProductCard;
